import { useState, useRef, useEffect } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card } from "../components/Shared";
import { toast } from "sonner";
import {
  ChartLineUp, Brain, UsersThree, Trophy, Compass, MapTrifold,
  FlowArrow, Robot, Note, Megaphone, ChatCircleDots, ListChecks, Clock,
  Gear, BookOpen, Lightning, Lifebuoy, ShieldCheck, ArrowSquareOut,
  Sparkle, PaperPlaneTilt, X, ArrowsClockwise,
} from "@phosphor-icons/react";

const SECTIONS = [
  { id: "overview", label: "Vista general" },
  { id: "vision", label: "Visión" },
  { id: "comunidad", label: "Comunidad" },
  { id: "motor", label: "Motor conversacional" },
  { id: "operacion", label: "Operación" },
  { id: "sistema", label: "Sistema" },
  { id: "flujos", label: "Flujos típicos" },
  { id: "go-live", label: "Ir a producción" },
  { id: "faq", label: "Preguntas frecuentes" },
];

export default function Manual() {
  const [active, setActive] = useState("overview");
  const scrollTo = (id) => {
    setActive(id);
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <PageHeader
        label="Documentación · guía de uso"
        title="Manual de uso"
        description="Cómo opera el Community Engine de Heroes Migrantes — qué hace cada módulo, cuándo usarlo y cuáles son los flujos típicos."
      />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          {/* TOC */}
          <aside className="hidden lg:block sticky top-6 self-start">
            <div className="section-label mb-2">Contenidos</div>
            <ul className="space-y-1">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => scrollTo(s.id)}
                    data-testid={`toc-${s.id}`}
                    className={`block w-full text-left text-sm py-1.5 px-2 rounded transition-colors ${
                      active === s.id
                        ? "text-terracotta font-medium bg-terracotta/5"
                        : "text-carbon-700 hover:text-terracotta"
                    }`}
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="space-y-6">
            <Section id="overview" icon={BookOpen} title="Vista general">
              <p>
                Heroes Migrantes Community Engine es un <strong>sistema operativo comunitario</strong> que combina
                un CRM conversacional con una capa de inteligencia artificial autónoma. Su misión: capturar líderes
                migrantes (migrant-side, USA) y embajadores territoriales (origin-side, en su país de origen),
                acompañarlos por WhatsApp con agentes IA y medir continuamente liderazgo, engagement, salud
                territorial, afinidad emocional y riesgo de moderación.
              </p>
              <Callout tone="terracotta" title="Arquitectura clave">
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>UnifiedUser</strong>: una sola tabla de personas — la misma persona puede ser founder Y embajadora.</li>
                  <li><strong>Pipelines independientes</strong>: cada pipeline (Founder, Ambassador) tiene sus propios stages, agentes y campañas.</li>
                  <li><strong>AI Intelligence Layer</strong>: 6 módulos que analizan y deciden — todos justifican sus decisiones (reasoning).</li>
                  <li><strong>Acciones automatizadas</strong>: la IA puede agregar tags y roles automáticamente; auto-remove solo si lo activás explícitamente.</li>
                </ul>
              </Callout>
            </Section>

            <Section id="vision" icon={ChartLineUp} title="Visión · vistas ejecutivas">
              <Module icon={ChartLineUp} name="Dashboard" route="/" >
                Resumen del estado del sistema: usuarios totales, pipelines activos, mensajes en 24h, top territorios. Es el primer pantallazo cuando entrás cada mañana.
              </Module>
              <Module icon={Brain} name="AI Intelligence" route="/intelligence" badge="módulo estrella">
                El cerebro analítico. Top líderes detectados, founders core, embajadores activos, dormidos, alto
                riesgo de moderación, salud territorial. Botones rápidos: <em>Recalcular territorios</em>,{" "}
                <em>Generar highlights</em>, <em>Correr ciclo IA</em> y <em>Config</em> (thresholds y pesos).
                <Tip>El ciclo IA corre automáticamente a las 05:00 hora Guatemala (11:00 UTC). El botón "Correr ciclo IA" lo dispara on-demand.</Tip>
              </Module>
            </Section>

            <Section id="comunidad" icon={UsersThree} title="Comunidad · las personas y su organización">
              <Module icon={UsersThree} name="Usuarios" route="/users">
                Base unificada (UnifiedUser). Buscar por nombre/WhatsApp/email, filtrar por user_side (migrant/origin),
                rol o estado. Click en un usuario abre su perfil completo con timeline IA, conversaciones y scores.
              </Module>
              <Module icon={Trophy} name="Founder Pipeline" route="/founder" badge="migrant-side">
                Pipeline para migrantes en USA. Captura desde Meta lead form, scoring conversacional, segmentación
                por <em>priority_high/medium/future_base</em>, generación de digests colectivos (privacy redacted),
                reactivaciones automáticas a 7/15/30 días.
              </Module>
              <Module icon={Compass} name="Ambassador Pipeline" route="/ambassador" badge="origin-side">
                Pipeline para líderes territoriales en sus países de origen. 18 stages canónicos: postulante → evaluado
                → video → entrevista → acuerdo → inducción → activo. Tipos A/B/C/D (territorial/contenido/comunidad/operativo).
                Check-ins biweekly (martes+viernes) cuando son activos.
              </Module>
              <Module icon={MapTrifold} name="Territorios" route="/territories">
                Vista geográfica. Cada municipio tiene un <code>municipality_health_score</code> (0-1) calculado por
                usuarios activos + embajadores + engagement promedio + crecimiento + contenido generado.
              </Module>
            </Section>

            <Section id="motor" icon={FlowArrow} title="Motor conversacional · qué se envía y cómo">
              <Module icon={FlowArrow} name="Editor de pipelines" route="/pipelines">
                Crear/editar pipelines y sus stages. Definir qué agente IA habla en cada stage, qué triggers
                disparan el movimiento al siguiente y qué condiciones aplican.
              </Module>
              <Module icon={Robot} name="Prompts & agentes" route="/agents">
                Catálogo de agentes IA con sus system_prompts, behavioral_rules, forbidden_topics, provider/modelo y
                versionado completo. Podés hacer <strong>rollback</strong> a una versión anterior con un click.
              </Module>
              <Module icon={Note} name="Templates WA" route="/templates">
                Templates de WhatsApp aprobados por Meta. Necesarios para enviar mensajes fuera de la ventana de 24h.
                Flujo: <em>draft</em> → <em>submitted</em> a Meta → <em>approved/rejected</em>.
                <Tip>Mínimo recomendado: 4 templates (bienvenida, reactivación, dinámica diaria, recordatorio embajador).</Tip>
              </Module>
              <Module icon={Megaphone} name="Campañas" route="/campaigns">
                Broadcasts masivos a segmentos. Antes de mandar siempre usá <strong>Dry-run</strong> para ver la
                audiencia exacta y un sample del mensaje. Si hay usuarios fuera de la ventana de 24h, la campaña
                solo les envía si tiene un template aprobado vinculado.
              </Module>
            </Section>

            <Section id="operacion" icon={Lightning} title="Operación · día a día y debug">
              <Module icon={ChatCircleDots} name="Simulador WhatsApp" route="/whatsapp">
                Simula inbound desde un usuario para probar el flujo end-to-end sin gastar mensajes reales. Muy útil
                para validar prompts nuevos antes de activarlos.
              </Module>
              <Module icon={ListChecks} name="Auditoría IA" route="/ai-logs">
                Cada decisión IA queda registrada con: qué detectó, por qué tomó la decisión, nivel de confianza y
                proveedor/modelo usado. Filtrable por tipo de decisión o usuario.
              </Module>
              <Module icon={Clock} name="Scheduler" route="/scheduler">
                Jobs programados (cron y one-time). El ciclo IA diario aparece acá. Podés inspeccionar fallos y
                relanzar jobs manualmente.
              </Module>
            </Section>

            <Section id="sistema" icon={Gear} title="Sistema · configuración global">
              <Module icon={BookOpen} name="Manual de uso" route="/manual">
                Esta misma página. Tenela a mano durante el onboarding del equipo.
              </Module>
              <Module icon={Gear} name="Configuración" route="/settings">
                LLM defaults (OpenAI/Anthropic API keys del tenant), provider de WhatsApp (mock vs
                Dialog360 producción), credenciales 360Dialog (api_key + phone_number_id) y rate limits.
              </Module>
            </Section>

            <Section id="flujos" icon={Lifebuoy} title="Flujos típicos">
              <Flow title="1. Capturar un nuevo founder">
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Lead llega vía Meta Ads → webhook <code>POST /api/founder/capture</code> con name + WhatsApp + ad_id.</li>
                  <li>El agente <em>Founder Capture</em> envía mensaje IA en &lt;5 minutos.</li>
                  <li>Conversación entra al pipeline; cada respuesta del usuario dispara <code>score_inbound</code>.</li>
                  <li>Cuando el score cruza el threshold, el usuario pasa a <em>priority_high</em> y se envía nota interna.</li>
                </ol>
              </Flow>
              <Flow title="2. Capturar un embajador origin-side">
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Desde <em>Ambassador Pipeline</em> → "Capturar postulante" → datos + opción de score inmediato.</li>
                  <li>AmbassadorTerritorialScoringAgent evalúa 0-100 + tipo A/B/C/D + red_flags.</li>
                  <li>Score ≥ 70 → priority_high → pedir video. Score 55-69 → priority_medium → pedir video. 40-54 → base futura. &lt;40 → filtrado.</li>
                  <li>Tras video recibido, agendar entrevista humana. Aprobada → acuerdo → firmado → inducción → activo.</li>
                  <li>Activado: check-ins automáticos martes + viernes; si pasa 14 días sin actividad → ambassador_inactive.</li>
                </ol>
              </Flow>
              <Flow title="3. Lanzar una campaña masiva sin sustos">
                <ol className="list-decimal pl-5 space-y-1">
                  <li>En <em>Campañas</em> → crear/editar; definir segmento (roles, states, países, stages).</li>
                  <li>Click "Dry-run": revisar audience_size y sample_message. Si está mal → ajustar segmento.</li>
                  <li>Verificar que los usuarios fuera de 24h tienen template aprobado vinculado.</li>
                  <li>Lanzar campaña. Rate limits cortan automáticamente para evitar spam.</li>
                  <li>Verificar <em>delivery stats</em> 1 hora después en Auditoría IA o el endpoint <code>/api/whatsapp/delivery/stats</code>.</li>
                </ol>
              </Flow>
              <Flow title="4. Investigar a un usuario sospechoso">
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Buscar usuario en <em>Usuarios</em> → abrir perfil.</li>
                  <li>Pulsar "Analizar con IA" → corre los 4 módulos (engagement, leadership, emotional, moderation).</li>
                  <li>Revisar <em>AI Intelligence Timeline</em>: signals + reasoning + acciones automatizadas.</li>
                  <li>Si <code>moderation_risk_score</code> ≥ 0.5 → ya se le asignó tag <em>flagged_for_review</em>. Decidir si remover manualmente.</li>
                </ol>
              </Flow>
            </Section>

            <Section id="go-live" icon={ShieldCheck} title="Ir a producción (go-live)">
              <p>
                Antes de activar la pauta de Meta, todos los items del checklist en{" "}
                <code>GET /api/admin/go-live-checklist</code> deben estar en ✓. Lista resumida:
              </p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Provider WhatsApp en <code>dialog360</code> (no <code>mock</code>).</li>
                <li>Credenciales <code>dialog360_api_key</code> + <code>phone_number_id</code> en <em>Configuración</em>.</li>
                <li>Env vars <code>WHATSAPP_VERIFY_TOKEN</code> + <code>WHATSAPP_WEBHOOK_SECRET</code> en backend.</li>
                <li>Webhook configurado en panel 360Dialog → <code>${"{"}REACT_APP_BACKEND_URL{"}"}/api/whatsapp/webhook/dialog360</code>.</li>
                <li>≥1 template aprobado en <em>Templates WA</em>.</li>
                <li>Backup MongoDB reciente (≤7 días): <code>POST /api/admin/backup</code>.</li>
                <li>E2E wizard pasó: <code>POST /api/admin/e2e/run</code> con num_users=5.</li>
                <li>Probar con 2 números reales del equipo antes de pautar.</li>
              </ol>
              <Callout tone="forest" title="Hoja de ruta detallada">
                Ver <code>/app/memory/GO_LIVE_CHECKLIST.md</code> en el repo.
              </Callout>
            </Section>

            <Section id="faq" icon={BookOpen} title="Preguntas frecuentes">
              <FaqRow q="¿Por qué la IA marcó a alguien como dormant_candidate?">
                Porque su <em>engagement_score</em> cayó por debajo del threshold de "medium" (configurable en
                AI Intelligence → Config). La IA lo etiqueta pero no lo cambia de estado.
              </FaqRow>
              <FaqRow q="¿Cuándo se ejecutan auto-remove?">
                Solo si <em>auto_moderation_enabled = true</em> en config Y el riesgo de moderación cruza el
                threshold de auto_remove (default 0.85) Y la confianza del modelo es ≥ <em>min_action_confidence</em>.
                Por defecto está apagado.
              </FaqRow>
              <FaqRow q="¿Puedo deshacer un rollback de prompt?">
                Sí. Cada rollback queda en el historial del agente; podés rollbackear de nuevo a la versión que tenía
                antes desde Prompts & agentes.
              </FaqRow>
              <FaqRow q="¿Qué pasa si un usuario manda un mensaje fuera del flujo del pipeline?">
                El agente activo del stage actual responde normalmente, y la respuesta entra al scoring. Si querés
                pausar al usuario, agregale el estado <em>paused</em> en su perfil.
              </FaqRow>
              <FaqRow q="¿Cómo exporto datos a Odoo?">
                Desde <em>Operación</em> hay endpoints de export: <code>/api/export/users.csv</code>,{" "}
                <code>/messages.csv</code>, <code>/territories.csv</code>, <code>/intelligence-signals.csv</code>.
                Headers Odoo-compatible (external_id, name, phone, etc.).
              </FaqRow>
              <FaqRow q="¿Dónde está el botón para subir un template a Meta?">
                Templates WA → crear draft → botón "Enviar a Meta" → cuando Meta apruebe, marcar como approved
                desde el botón "Marcar aprobado". El estado vive en nuestra DB pero la aprobación real ocurre en
                Meta Business Manager.
              </FaqRow>
              <FaqRow q="¿Cómo configuro horarios diferentes para entrevistas y capacitaciones?">
                Calendario → tab <em>Horarios por servicio</em> → cada servicio (Entrevista / Capacitación) tiene
                su propia tarjeta. Activá días, definí inicio/fin y duración del slot. La zona horaria por
                default es <code>America/Guatemala</code>.
              </FaqRow>
              <FaqRow q="¿Cómo bloqueo un día completo (por ejemplo un feriado)?">
                Calendario → tab <em>Bloqueos manuales</em> → "Nuevo bloqueo" → selecciona el servicio
                (o "Todos"), define inicio y fin, opcionalmente el motivo. Mientras dure el bloqueo, ese rango
                no aparecerá en el booking link público del lead.
              </FaqRow>
              <FaqRow q="¿Quién atendió una entrevista? ¿Quién envió un link de booking?">
                Está registrado en el card del evento (en /calendar al abrir el evento) bajo "Trazabilidad
                admin": <em>Link de booking enviado por</em> y <em>Atendida por</em>. Además, todas las
                acciones de escritura quedan en <em>Auditoría IA → Acciones de usuarios</em>.
              </FaqRow>
              <FaqRow q="¿Cómo le envío notas al lead junto al link de la reunión?">
                En el modal del evento → escribir las notas → marcar el checkbox{" "}
                <em>"Compartir estas notas con el lead en el mensaje de WhatsApp"</em> → "Atender y enviar
                link". Las notas se anexan al mensaje del lead como "📝 Nota del equipo: …".
              </FaqRow>
              <FaqRow q="¿Cómo veo qué admin cambió qué en la plataforma?">
                Auditoría IA → tab <em>Acciones de usuarios</em>. Muestra TODA escritura (POST/PUT/PATCH/DELETE)
                con timestamp, admin que lo hizo, método HTTP, endpoint, status y IP. Hay filtros por email,
                método y path.
              </FaqRow>
              <FaqRow q="¿Por qué el chatbot del manual no me responde?">
                El chatbot del manual usa tu API key de OpenAI/Anthropic configurada en{" "}
                <code>/settings</code>. Si no tenés key cargada, te dará error. Cargá una key en{" "}
                <em>Sistema → Configuración → LLM</em> y reintentá.
              </FaqRow>
            </Section>
          </div>
        </div>
      </PageBody>
      <ManualChatHelper />
    </>
  );
}

function Section({ id, icon: Icon, title, children }) {
  return (
    <Card className="p-6 scroll-mt-6" testid={`section-${id}`}>
      <div id={`section-${id}`} />
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-carbon-200">
        <Icon size={20} className="text-terracotta" />
        <h2 className="font-heading text-2xl">{title}</h2>
      </div>
      <div className="space-y-4 text-sm text-carbon-900 leading-relaxed">{children}</div>
    </Card>
  );
}

function Module({ icon: Icon, name, route, badge, children }) {
  return (
    <div className="border-l-2 border-carbon-200 pl-4 py-1 hover:border-terracotta transition-colors">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className="text-terracotta" />
        <span className="font-medium">{name}</span>
        <code className="text-[10px] font-mono bg-carbon-100 px-1.5 py-0.5 rounded text-carbon-700">{route}</code>
        {badge && <span className="text-[9px] uppercase tracking-wider bg-terracotta/10 text-terracotta px-1.5 py-0.5 rounded">{badge}</span>}
      </div>
      <div className="text-sm text-carbon-700">{children}</div>
    </div>
  );
}

function Tip({ children }) {
  return (
    <div className="mt-2 text-xs bg-warning/10 border border-warning/30 rounded px-3 py-2">
      <strong className="text-warning">Tip:</strong> {children}
    </div>
  );
}

function Callout({ tone = "neutral", title, children }) {
  const tones = {
    terracotta: "border-terracotta/30 bg-terracotta/5",
    forest: "border-forest/30 bg-forest/5",
    warning: "border-warning/30 bg-warning/5",
    neutral: "border-carbon-200 bg-carbon-50",
  };
  return (
    <div className={`border ${tones[tone]} rounded p-4 my-3`}>
      <div className="section-label mb-1">{title}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function Flow({ title, children }) {
  return (
    <div className="border border-carbon-200 rounded p-4">
      <div className="font-medium text-sm mb-2 text-terracotta">{title}</div>
      <div className="text-sm text-carbon-700">{children}</div>
    </div>
  );
}

function FaqRow({ q, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-carbon-200 last:border-0 py-2">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left">
        <span className="font-medium text-sm">{q}</span>
        <ArrowSquareOut size={14} className={`text-carbon-500 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="text-sm text-carbon-700 mt-2 pl-2 border-l-2 border-terracotta">{children}</div>}
    </div>
  );
}

// =====================================================================
// ManualChatHelper · widget flotante con LLM del tenant
// =====================================================================
function ManualChatHelper() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "¡Hola! Soy tu asistente del manual. Preguntame cómo usar cualquier módulo de la plataforma." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  }, [open, messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const newMsgs = [...messages, { role: "user", content: text }];
    setMessages(newMsgs);
    setInput("");
    setBusy(true);
    try {
      // Solo enviamos los últimos 10 turnos al backend (el system prompt ya tiene todo el conocimiento)
      const history = newMsgs
        .slice(0, -1)
        .filter((m) => m.role === "user" || m.role === "assistant")
        .slice(-10);
      const { data } = await api.post("/manual/chat", { message: text, history });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, model: data.model }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error consultando el asistente.";
      toast.error(detail);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `⚠️ ${detail}`, error: true },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => setMessages([{ role: "assistant", content: "Conversación reiniciada. ¿En qué te puedo ayudar?" }]);

  const suggestions = [
    "¿Cómo configuro horarios de entrevistas?",
    "¿Cómo veo qué admin atendió una cita?",
    "¿Cómo bloqueo un día?",
    "¿Cómo apruebo un template de WhatsApp?",
  ];

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="manual-chat-fab"
        className="fixed bottom-20 right-6 z-40 bg-terracotta text-white rounded-full shadow-lg hover:bg-terracotta-hover transition-all flex items-center gap-2 px-4 py-3 text-sm font-medium"
        aria-label="Abrir asistente del manual"
      >
        <Sparkle size={18} weight="fill" />
        Asistente del manual
      </button>
    );
  }

  return (
    <div
      data-testid="manual-chat-panel"
      className="fixed bottom-20 right-6 z-40 w-[380px] max-w-[calc(100vw-32px)] h-[560px] max-h-[calc(100vh-120px)] bg-surface border border-carbon-200 rounded-lg shadow-2xl flex flex-col"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-carbon-200 bg-terracotta text-white rounded-t-lg flex items-center gap-2">
        <Sparkle size={18} weight="fill" />
        <div className="flex-1">
          <div className="text-sm font-medium">Asistente del manual</div>
          <div className="text-[10px] opacity-80">Usa tu API key de LLM configurada</div>
        </div>
        <button onClick={reset} title="Reiniciar conversación" data-testid="manual-chat-reset"
          className="p-1 hover:bg-white/10 rounded">
          <ArrowsClockwise size={14} />
        </button>
        <button onClick={() => setOpen(false)} title="Cerrar" data-testid="manual-chat-close"
          className="p-1 hover:bg-white/10 rounded">
          <X size={16} weight="bold" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 scrollbar-thin">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words ${
                m.role === "user"
                  ? "bg-terracotta text-white"
                  : m.error
                    ? "bg-warning/10 border border-warning/30 text-carbon-900"
                    : "bg-carbon-100 text-carbon-900"
              }`}
            >
              {m.content}
              {m.model && (
                <div className="text-[9px] opacity-60 mt-1 font-mono">{m.model}</div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-carbon-100 px-3 py-2 rounded-lg text-sm text-carbon-500">
              <span className="inline-flex gap-1">
                <span className="animate-bounce">·</span>
                <span className="animate-bounce" style={{ animationDelay: "0.1s" }}>·</span>
                <span className="animate-bounce" style={{ animationDelay: "0.2s" }}>·</span>
              </span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Suggestions (solo cuando no hay conversación) */}
      {messages.length <= 1 && (
        <div className="px-3 pb-2 flex flex-wrap gap-1">
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => setInput(s)}
              data-testid={`manual-chat-suggest`}
              className="text-[10px] border border-carbon-200 hover:border-terracotta hover:text-terracotta rounded-full px-2 py-1 text-carbon-600"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-carbon-200 p-2 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Preguntame algo del manual…"
          data-testid="manual-chat-input"
          disabled={busy}
          className="flex-1 bg-page border border-carbon-200 rounded-full px-3 py-2 text-sm focus:outline-none focus:border-terracotta disabled:opacity-50"
        />
        <button
          onClick={send}
          disabled={busy || !input.trim()}
          data-testid="manual-chat-send"
          className="bg-terracotta text-white rounded-full w-9 h-9 flex items-center justify-center hover:bg-terracotta-hover disabled:opacity-50"
        >
          <PaperPlaneTilt size={14} weight="fill" />
        </button>
      </div>
    </div>
  );
}

